import { Base64 } from 'js-base64';
import { BehaviorSubject, Subscription } from 'rxjs';
import {
  menuItems,
  ONBOARDING_ONLY_MENU_KEYS,
} from 'src/app/core/constants/menuItems';
import { REPORT_TYPE_CATALOG } from 'src/app/core/constants/report-types';
import { funds } from 'src/app/core/constants/vehicle';
import { Imenu } from 'src/app/core/interfaces/menu';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import { AceAuthService } from 'src/app/core/services/auth/ace-auth.service';
import { TenantBrandingService } from 'src/app/core/services/tenant-branding.service';
import { UserService } from 'src/app/core/services/user.service';
import { ConfigService } from 'src/app/initializer/config.service';
import { ModalDialogData } from 'src/app/models/modelDialog.model';
import { DialogComponent } from 'src/app/shared/components/dialog/dialog.component';
import { TermsOfUseComponent } from 'src/app/shared/components/dialog/terms-of-use/terms-of-use.component';
import { BreakPointService } from 'src/app/shared/services/break-point.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { environment } from 'src/environments/environment';

import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { PontoService } from '../../settings/ponto/ponto.service';
import { TranslateService } from '@ngx-translate/core';
import { ChatNotificationService } from 'src/app/core/chat/chat-notification.service';
import {
  AceNotification,
  AceNotificationService,
} from 'src/app/core/services/ace-notification.service';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnInit, OnDestroy {
  baseData: any;
  private baseDataSubscription: Subscription;
  private readonly subscriptions = new Subscription();
  activeSubMenuPath = '';

  menuItems = menuItems;
  ddlfund = funds;
  selectedfund = funds[0].value;

  notifications: any[] = [];
  currentNotificationIndex = 0;
  showNotification = true;
  title = 'GP Portal';
  activePathName = '';

  // Detail routes keep the surrounding shell but receive an entity-specific
  // title. The collection routes themselves still use their normal menu title.
  isInvestorDetailRoute(): boolean {
    return /\/(onboarding|dashboard|investors)\/[^/?]+(?:\?|$)/.test(this.router.url);
  }

  detailPageTitle(): string | null {
    if (/\/onboarding\/[^/?]+(?:\?|$)/.test(this.router.url)) {
      return 'Onboarding Detail';
    }
    if (/\/(dashboard|investors)\/[^/?]+(?:\?|$)/.test(this.router.url)) {
      return 'Investor Detail';
    }
    return null;
  }
  isInaccessible = false;
  currentGPS: any;
  sessionUser: any = {};
  selectedInvestor: any;
  isSidebarCollapsed = false;
  investors: any[] = [];
  selectedFund: any;
  tenantLogoUrl: string | null = null;
  private sidebarLogoFailed = false;
  private visibleMenuItemsSource: any[] | null = null;
  private visibleMenuItemsCompactMode: boolean | null = null;
  private visibleMenuItemsValue: any[] = [];

  get sidebarLogoUrl(): string {
    if (!this.sidebarLogoFailed) {
      const fundLogo = [
        this.selectedFund?.imageUrl,
        this.selectedFund?.logoUrl,
      ].find((value: unknown) => typeof value === 'string' && value.trim());
      if (typeof fundLogo === 'string') {
        return fundLogo.trim();
      }
    }

    return (
      this.tenantLogoUrl?.trim() || '../assets/images/SidebarMenuButton.svg'
    );
  }

  get usesCompactSidebarLogoMark(): boolean {
    if (this.sidebarLogoFailed) return true;

    const fundLogo = [
      this.selectedFund?.imageUrl,
      this.selectedFund?.logoUrl,
    ].find((value: unknown) => typeof value === 'string' && value.trim());

    return !fundLogo && !this.tenantLogoUrl?.trim();
  }

  /**
   * Gates the header's quick-task button. Privileges hang off the selected
   * fund rather than a shared service, and a task you can't then open on the
   * board is a dead end — so the button follows the Management menu item.
   */
  get canCreateTasks(): boolean {
    return !!this.selectedFund?.privileges?.includes('viewManagement');
  }

  openChatCreatePanel(): void {
    this.chatNotifications.requestCreateChat();
  }

  get sessionUserName(): string {
    const name = [this.sessionUser?.firstName, this.sessionUser?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    return name || this.sessionUser?.title || 'Account';
  }

  get sessionUserInitials(): string {
    const initials = [this.sessionUser?.firstName, this.sessionUser?.lastName]
      .filter(Boolean)
      .map((part: string) => part.charAt(0).toUpperCase())
      .join('');
    return initials || this.sessionUserName.charAt(0).toUpperCase();
  }

  persistInvestor: any;
  persistFund: any;
  funds: any[] = [];
  privilegesOrder = [
    'viewDashboard',
    'viewManagement',
    'viewTransactions',
    'viewReports',
    'viewDocuments',
    'viewPortfolioCompanies',
    'viewQuarterlyData',
    'viewInvestors',
    'viewOnboarding',
    'viewChats',
    'viewFaq',
    'viewTeam',
    'viewChat',
    'viewSettings',
  ];

  selectedMenu: any;
  selectedSubMenu: any;
  isInvestorSelectOpen = false;
  isFundSelectOpen = false;
  isMobileInvestorSelectOpen = false;
  isMainContentScrolled = false;
  isNotificationPanelOpen = false;
  readonly chatUnreadCount$ = this.chatNotifications.unreadCount$;
  readonly aceNotifications$ = this.aceNotifications.notifications$;
  readonly aceUnreadCount$ = this.aceNotifications.unreadCount$;
  readonly aceNotificationsLoading$ = this.aceNotifications.loading$;
  readonly aceNotificationsError$ = this.aceNotifications.error$;

  get usesEdgeToEdgePageShell(): boolean {
    const pagePath = this.router.url.split('?')[0];
    return (
      pagePath.endsWith('/management') ||
      pagePath.endsWith('/chat') ||
      pagePath.endsWith('/team') ||
      pagePath.includes('/team/') ||
      pagePath.endsWith('/investors') ||
      pagePath.includes('/investors/')
    );
  }

  get usesCompactSidebarShell(): boolean {
    const pagePath = this.router.url.split('?')[0];
    return /\/(dashboard|onboarding)(?:\/|$)/.test(pagePath);
  }

  get visibleMenuItems(): any[] {
    const compactMode =
      environment.onboardingOnlyMode || this.usesCompactSidebarShell;
    if (
      this.visibleMenuItemsSource === this.menuItems &&
      this.visibleMenuItemsCompactMode === compactMode
    ) {
      return this.visibleMenuItemsValue;
    }

    this.visibleMenuItemsSource = this.menuItems;
    this.visibleMenuItemsCompactMode = compactMode;
    this.visibleMenuItemsValue = compactMode
      ? this.menuItems
          .filter(item => ONBOARDING_ONLY_MENU_KEYS.includes(item.key))
          .map(item =>
            this.hasSubMenus(item)
              ? {
                  ...item,
                  subMenus: (item.subMenus || []).filter((subItem: any) =>
                    ONBOARDING_ONLY_MENU_KEYS.includes(subItem.key)
                  ),
                }
              : item
          )
      : this.menuItems;
    return this.visibleMenuItemsValue;
  }

  get isChatPage(): boolean {
    return this.router.url.split('?')[0].endsWith('/chat');
  }

  onMainContentScroll(event: Event): void {
    const scrollTop = (event.target as HTMLElement).scrollTop;
    this.isMainContentScrolled = scrollTop > 4;
  }

  // Toggle the sidebar between collapsed and expanded states
  toggleSidebar(): void {
    this.chatNotifications.toggleSidebar();
  }

  closeNotification(): void {
    this.showNotification = false;
    this.currentNotificationIndex++;

    if (this.currentNotificationIndex < this.notifications.length) {
      setTimeout(() => {
        this.showNotification = true;
      }, 30000); // 30 seconds
    }
  }

  private urlData = new BehaviorSubject({ bigUrl: '', smallUrl: '' });
  urlDataObservable$ = this.urlData.asObservable();

  public async setUrlData(data: any) {
    if (data) {
      this.urlData.next(data);
    }
  }

  constructor(
    private router: Router,
    public breakPointService: BreakPointService,
    public dialog: MatDialog,
    private authService: AuthService,
    public commonService: CommonService,
    public userService: UserService,
    private config: ConfigService,
    private pontoService: PontoService,
    private translate: TranslateService,
    private aceAuthService: AceAuthService,
    private tenantBrandingService: TenantBrandingService,
    private readonly aceNotifications: AceNotificationService,
    public readonly chatNotifications: ChatNotificationService
  ) {
    this.subscriptions.add(
      this.tenantBrandingService.state$.subscribe(state => {
        this.tenantLogoUrl = state.theme?.logoUrl?.trim() || null;
      })
    );
    this.subscriptions.add(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.syncTenantBrandingForRoute(
            event.urlAfterRedirects || event.url
          );
          this.handleNavigationEnd(event);
          if (
            this.selectedInvestor?.id &&
            this.selectedFund?.id &&
            !event.url.includes(
              `gp/${this.selectedInvestor.id}/vehicle/${this.selectedFund.id}/quarterly-data`
            )
          ) {
            localStorage.removeItem('search');
            localStorage.removeItem('filterValue');
          }
        } else if (event instanceof NavigationStart) {
          this.handleNavigationStart(event);
        }
      })
    );
  }

  onSelectedMenu(value: any): void {
    if (!value) {
      return;
    }

    this.selectedMenu = value;

    if (!this.hasSubMenus(value)) {
      this.selectedSubMenu = undefined;
      this.activeSubMenuPath = '';
      this.menuItems.forEach(item => {
        if (this.hasSubMenus(item)) {
          item.isToggled = false;
        }
      });
    }
  }

  toggleMenu(item: any): void {
    if (!this.hasSubMenus(item)) {
      return;
    }

    item.isToggled = !item.isToggled;
  }

  hasSubMenus(item: any): boolean {
    return Array.isArray(item?.subMenus) && item.subMenus.length > 0;
  }

  isActiveMenu(item: any): boolean {
    if (!item) {
      return false;
    }

    const routePath = this.getPathSegments(this.router.url)[5];
    if (routePath) {
      return (
        item.path === routePath ||
        (this.hasSubMenus(item) &&
          item.subMenus.some(
            (subItem: any) => subItem.path === this.activeSubMenuPath
          ))
      );
    }

    return (
      item.path === this.selectedMenu?.path ||
      (this.hasSubMenus(item) &&
        item.subMenus.some(
          (subItem: any) => subItem.path === this.activeSubMenuPath
        ))
    );
  }

  navigateToComponent(data: Imenu) {
    if (!this.selectedInvestor?.id || !this.selectedFund?.id) {
      return;
    }
    this.router.navigate([
      `gp/${this.selectedInvestor.id}/vehicle/${this.selectedFund.id}/${data.path}`,
    ]);
  }

  navigateToSubmenu(path: string) {
    if (!this.selectedInvestor?.id || !this.selectedFund?.id) {
      return;
    }
    this.router.navigate([
      `gp/${this.selectedInvestor.id}/vehicle/${this.selectedFund.id}/${path}`,
    ]);
  }

  onVehicleChange(value: any) {
    if (!this.selectedInvestor?.id || !value?.id || !this.selectedMenu?.path) {
      return;
    }
    this.syncTenantBranding(this.selectedInvestor, value);
    this.router.navigate([
      `gp/${this.selectedInvestor.id}/vehicle/${value.id}/${
        this.selectedMenu.path
      }${this.selectedSubMenu ? '/' + this.selectedSubMenu : ''}`,
    ]);
  }

  onGPSChange(value: any) {
    if (!value?.id || !value?.vehicles?.[0]?.id || !this.selectedMenu?.path) {
      return;
    }
    const selectedVehicle = value.vehicles[0];
    this.syncTenantBranding(value, selectedVehicle);
    this.router.navigate([
      `gp/${value.id}/vehicle/${selectedVehicle.id}/${this.selectedMenu.path}${
        this.selectedSubMenu ? '/' + this.selectedSubMenu : ''
      }`,
    ]);
  }

  // Checks if a submenu item is currently active based on URL
  isActiveSubMenu(item: any, subItem: any): boolean {
    return (
      this.activeSubMenuPath === subItem.path &&
      this.selectedMenu?.path === item.path
    );
  }

  private loadNotifications(): void {
    // Onboarding-only view: drop the header notification banner ("Important
    // information" and the two that follow it). These are hardcoded demo
    // notifications in the i18n files, not real signals. Leaving the list empty
    // takes the existing `else notificationSpacer` branch, which is the same
    // layout the header already uses once a user dismisses all of them.
    if (environment.onboardingOnlyMode) {
      this.notifications = [];
      this.showNotification = false;
      return;
    }
    this.translate
      .get('translations.dashboard.notifications')
      .subscribe((notifications: any) => {
        this.notifications = notifications;
        this.currentNotificationIndex = 0;
        this.showNotification = this.notifications.length > 0;
      });
  }

  ngOnInit() {
    this.chatNotifications.start();
    this.subscriptions.add(
      this.chatNotifications.sidebarCollapsed$.subscribe(collapsed =>
        (this.isSidebarCollapsed = collapsed)
      )
    );
    this.refreshAceNotifications();
    this.loadNotifications();
    this.selectedMenu = this.menuItems[0];

    this.subscriptions.add(
      this.translate.onLangChange.subscribe(() => this.loadNotifications())
    );
    this.baseDataSubscription = this.config.baseDataObservable$.subscribe(
      (res: any) => {
        if (res) {
          this.baseData = res;
          this.investors = Array.isArray(res.gps) ? res.gps : [];
          this.funds = this.investors[0]?.vehicles
            ? this.reorderPrivileges(this.investors[0].vehicles)
            : [];
          this.syncTenantBrandingForCurrentRoute();

          const pendingPath = localStorage.getItem('tpKey');
          if (pendingPath) {
            localStorage.removeItem('tpKey');
            if (pendingPath.startsWith('/') && !pendingPath.startsWith('//')) {
              void this.router.navigateByUrl(pendingPath);
            }
          }
        }
      }
    );
    this.subscriptions.add(
      this.userService.userDataObservable$.subscribe((res: any) => {
        this.sessionUser = res;
        if (!res.events.confirmedTermsOfUse) {
          this.openTermsOfUse();
        }
      })
    );

    this.subscriptions.add(
      this.userService.resetValuesObservable$.subscribe((res: any) => {
        if (
          res === 'reset' &&
          this.selectedFund?.id &&
          this.persistFund?.id &&
          this.selectedInvestor?.id &&
          this.persistInvestor?.id &&
          (this.selectedFund.id !== this.persistFund.id ||
            this.selectedInvestor.id !== this.persistInvestor.id)
        ) {
          this.selectedFund = this.persistFund;
          this.selectedInvestor = this.persistInvestor;
        }
      })
    );
  }

  @HostListener('document:keydown.escape')
  dismissChatPopup(): void {
    this.chatNotifications.dismissPopup();
    this.isNotificationPanelOpen = false;
  }

  @HostListener('document:click', ['$event'])
  dismissNotificationPanel(event: MouseEvent): void {
    const target = event.target;
    if (target instanceof Element && target.closest('.notification-control')) {
      return;
    }
    this.isNotificationPanelOpen = false;
  }

  toggleNotificationPanel(event: MouseEvent): void {
    event.stopPropagation();
    this.isNotificationPanelOpen = !this.isNotificationPanelOpen;
    if (this.isNotificationPanelOpen) {
      this.refreshAceNotifications();
    }
  }

  markAllAceNotificationsAsRead(event: MouseEvent): void {
    event.stopPropagation();
    this.subscriptions.add(
      this.aceNotifications
        .markAllAsRead()
        .subscribe({ error: () => undefined })
    );
  }

  openAceNotification(notification: AceNotification, event: MouseEvent): void {
    event.stopPropagation();
    this.subscriptions.add(
      this.aceNotifications.markAsRead(notification.id).subscribe({
        next: () => this.navigateFromAceNotification(notification),
        error: () => this.navigateFromAceNotification(notification),
      })
    );
  }

  refreshAceNotifications(): void {
    this.subscriptions.add(
      this.aceNotifications.refresh().subscribe({ error: () => undefined })
    );
  }

  private navigateFromAceNotification(notification: AceNotification): void {
    const data = notification.data || {};
    const candidate = [data.deepLink, data.url, data.route].find(
      value => typeof value === 'string'
    );
    if (
      typeof candidate !== 'string' ||
      !candidate.startsWith('/') ||
      candidate.startsWith('//') ||
      candidate.includes('://')
    ) {
      return;
    }
    this.isNotificationPanelOpen = false;
    void this.router.navigateByUrl(candidate);
  }

  notificationIcon(type: string): string {
    const normalizedType = type.toUpperCase();
    if (normalizedType.includes('CAPITAL_CALL'))
      return 'account_balance_wallet';
    if (normalizedType.includes('TASK')) return 'task_alt';
    if (normalizedType.includes('DOCUMENT')) return 'description';
    return 'notifications';
  }

  notificationIconTone(type: string): string {
    const normalizedType = type.toUpperCase();
    if (normalizedType.includes('CAPITAL_CALL')) {
      return 'notification-icon-capital-call';
    }
    if (normalizedType.includes('TASK')) return 'notification-icon-task';
    if (normalizedType.includes('DOCUMENT'))
      return 'notification-icon-document';
    return 'notification-icon-default';
  }

  reorderPrivileges(data: any) {
    return data.map((item: any) => {
      // Team and Messages use the ACE backend. Until base-data supplies their
      // own privileges, expose both only where that backend is configured.
      if (environment.teamApiUrl) {
        const acePrivileges = ['viewTeam', 'viewChat'];
        item.privileges = [
          ...item.privileges,
          ...acePrivileges.filter(
            privilege => !item.privileges.includes(privilege)
          ),
        ];
      }
      // PoC: same story for Onboarding -- the page exists and is routed, but no
      // `viewOnboarding` privilege is issued by base-data, so force it on for the
      // onboarding-only view. Without this the filter below drops the new nav item.
      if (
        environment.onboardingOnlyMode &&
        !item.privileges.includes('viewOnboarding')
      ) {
        item.privileges = [...item.privileges, 'viewOnboarding'];
      }
      // PoC: ditto for the Chats and FAQ placeholder pages.
      if (
        environment.onboardingOnlyMode &&
        !item.privileges.includes('viewChats')
      ) {
        item.privileges = [...item.privileges, 'viewChats'];
      }
      if (
        environment.onboardingOnlyMode &&
        !item.privileges.includes('viewFaq')
      ) {
        item.privileges = [...item.privileges, 'viewFaq'];
      }
      item.privileges = this.privilegesOrder.filter(privilege =>
        item.privileges.includes(privilege)
      );
      return item;
    });
  }

  changeTheme() {
    this.syncTenantBranding(this.selectedInvestor, this.selectedFund);
  }

  onTenantLogoError(): void {
    this.tenantLogoUrl = null;
  }

  private syncTenantBrandingForCurrentRoute(): void {
    this.syncTenantBrandingForRoute(this.router.url);
  }

  private syncTenantBrandingForRoute(url: string): void {
    const urlSegments = this.getPathSegments(url);
    const gpId = urlSegments[2];
    const vehicleId = urlSegments[4];
    const routeInvestor = this.baseData?.gps?.find(
      (investor: any) => investor.id === gpId
    );
    const routeFund = routeInvestor?.vehicles?.find(
      (fund: any) => fund.id === vehicleId
    );
    if (routeInvestor) {
      this.selectedInvestor = routeInvestor;
      this.funds = this.reorderPrivileges(routeInvestor.vehicles || []);
    }
    if (routeFund) {
      this.selectedFund = routeFund;
    }
    const investor = routeInvestor || this.selectedInvestor;
    const fund = routeFund || this.selectedFund;
    if (!investor || !fund) {
      return;
    }

    this.syncTenantBranding(investor, fund);
  }

  onSidebarLogoError(): void {
    this.sidebarLogoFailed = true;
  }

  private syncTenantBranding(investor: any, fund: any): void {
    this.sidebarLogoFailed = false;
    const directoryMatch = this.config
      .getAceFundDirectory()
      .find(
        item =>
          item.vehicleId === fund?.id ||
          item.id === fund?.id ||
          item.id === fund?.aceFundId
      );
    const tenantId =
      directoryMatch?.tenantId || fund?.tenantId || investor?.tenantId || null;
    const fallback =
      fund?.colors || fund?.branding?.colors || investor?.colors || {};
    this.aceAuthService.setTenantId(tenantId);
    try {
      if (tenantId) {
        localStorage.setItem('selectedTenantId', tenantId);
      } else {
        localStorage.removeItem('selectedTenantId');
      }
    } catch {
      // Ignore storage failures in restricted browser contexts.
    }
    this.subscriptions.add(
      this.tenantBrandingService
        .loadForTenant(tenantId, {
          primaryColor: fallback.primary,
          primaryLightColor: fallback.primaryLight,
          secondaryColor: fallback.secondary,
          secondaryLightColor: fallback.secondaryLight,
        })
        .subscribe()
    );
  }

  handleNavigationEnd(event: NavigationEnd) {
    // Strips query string (Keycloak 26+ `iss` param) and fragment before splitting.
    const urlSegments = this.getPathSegments(
      event.urlAfterRedirects || event.url
    );
    const gpName = urlSegments[1];
    const gpId = urlSegments[2];
    const vehicleName = urlSegments[3];
    const vehicleId = urlSegments[4];
    const tabName = urlSegments[5];
    const tabParam = urlSegments[6]
      ? urlSegments[6].split('?')[0]
      : urlSegments[6];
    if (!this.baseData?.gps?.length) {
      return;
    }
    const isTeamRoute = tabName === 'team';
    const isTeamDetailRoute = isTeamRoute && Boolean(tabParam);
    this.activeSubMenuPath = isTeamDetailRoute ? '' : tabParam || '';
    // Save previous toggle states
    const toggleMap: any = {};
    (this.menuItems || []).forEach(item => {
      if ('isToggled' in item) {
        toggleMap[item.key] = item.isToggled;
      }
    });

    this.menuItems = JSON.parse(JSON.stringify(menuItems));

    // Restore toggle states
    this.menuItems.forEach(item => {
      if (toggleMap[item.key] !== undefined) {
        item.isToggled = toggleMap[item.key];
      }
    });

    // Detail views keep the same collapsed navigation state as the dashboard.
    // Do not carry an expanded group from a previous page into the detail shell.
    if (this.isInvestorDetailRoute()) {
      this.menuItems.forEach(item => {
        if ('isToggled' in item) {
          item.isToggled = false;
        }
      });
    }

    const isValidGpId = () =>
      this.baseData?.gps?.find((x: any) => x.id === gpId);
    const isValidVehicleId = () =>
      this.selectedInvestor?.vehicles?.find((x: any) => x.id === vehicleId);

    if (gpName === 'gp' && vehicleName === 'vehicle') {
      if (isValidGpId()) {
        this.selectedInvestor = isValidGpId();
        this.funds = this.reorderPrivileges(this.selectedInvestor.vehicles);

        if (!isValidVehicleId()) {
          // Valid GP ID but invalid Vehicle ID
          this.selectedFund = this.reorderPrivileges(
            this.selectedInvestor.vehicles
          )[0];
          this.selectedMenu = this.menuItems.filter(
            x => x.key === this.selectedFund.privileges[0]
          )[0];

          if (urlSegments.length === 4) {
            this.router.navigate([
              `gp/${this.selectedInvestor.id}/vehicle/${this.selectedFund.id}/${this.selectedMenu.path}`,
            ]);
          } else {
            const urlRedirect = `gp/${this.selectedInvestor.id}/vehicle/${this.selectedFund.id}/${this.selectedMenu.path}`;
            this.openDialog(true, urlRedirect);
          }
        } else {
          // Valid GP and valid Vehicle ID
          this.selectedFund = isValidVehicleId();
          this.selectedMenu =
            // Standard match — top-level privileged menus (dashboard, management etc.)
            this.menuItems
              .filter(
                x =>
                  this.selectedFund.privileges.find((y: any) => y === x.key) !==
                  undefined
              )
              .filter(z => z.path === tabName)[0] ||
            // Group menu match — portfolio-group, workspace-group
            this.menuItems.find(
              x =>
                x.path === tabName &&
                x.subMenus?.some(
                  (sub: any) =>
                    this.selectedFund.privileges.includes(sub.key) ||
                    this.selectedFund.subPrivilegeIds?.includes(sub.key)
                )
            ) ||
            // Submenu match (when parent path is missing in URL)
            this.menuItems.find(x =>
              x.subMenus?.some(
                (sub: any) =>
                  sub.path === tabName &&
                  (this.selectedFund.privileges.includes(sub.key) ||
                    this.selectedFund.subPrivilegeIds?.includes(sub.key))
              )
            ) ||
            // Fallback to first privileged menu
            this.menuItems.filter(
              x => x.key === this.selectedFund.privileges[0]
            )[0];

          if (isTeamRoute) {
            // Team routes have no submenu. Reset both the selected path and any
            // submenu that was left expanded by the previous page.
            this.selectedSubMenu = undefined;
            this.menuItems.forEach(item => {
              if ('subMenus' in item) item.isToggled = false;
            });
          } else if (urlSegments.length <= 6) {
            // Short URL: /gp/{gpId}/vehicle/{vehicleId}/{tabName} or less
            if (this.isValidPath(tabName) || tabName === undefined) {
              const subMenus = this.filterValidSubmenus();
              const url = `gp/${this.selectedInvestor.id}/vehicle/${
                this.selectedFund.id
              }/${
                subMenus.length > 0
                  ? `${this.selectedMenu.path}/${subMenus[0].path}`
                  : `${this.selectedMenu.path}`
              }`;
              this.router.navigate([url], { queryParamsHandling: 'preserve' });
            } else {
              const urlRedirect = `gp/${this.selectedInvestor.id}/vehicle/${this.selectedFund.id}/${this.selectedMenu.path}`;
              this.openDialog(true, urlRedirect);
            }
          } else if (urlSegments.length === 7) {
            // Medium URL: /gp/{gpId}/vehicle/{vehicleId}/{tabName}/{tabParam}
            // e.g. /portfolio-group/quarterly-data  or  /transactions/bank
            this.menuItems.forEach(item => {
              if ('subMenus' in item && item.path === this.selectedMenu.path) {
                item.isToggled = true;
              }
            });

            if (this.isValidPath(tabName)) {
              const subMenus = this.filterValidSubmenus();
              if (subMenus.some((x: any) => x.path === tabParam)) {
                // Valid submenu — navigate to it
                const url = `gp/${this.selectedInvestor.id}/vehicle/${this.selectedFund.id}/${this.selectedMenu.path}/${tabParam}`;
                this.selectedSubMenu = tabParam;
                this.activeSubMenuPath = tabParam;
                this.router.navigate([url], {
                  queryParamsHandling: 'preserve',
                });
              } else if (subMenus.length > 0) {
                // tabParam is not a valid submenu — redirect to first valid submenu
                const firstSubmenuPath = subMenus[0].path;
                const urlRedirect = `gp/${this.selectedInvestor.id}/vehicle/${this.selectedFund.id}/${this.selectedMenu.path}/${firstSubmenuPath}`;
                this.selectedSubMenu = firstSubmenuPath;
                this.activeSubMenuPath = firstSubmenuPath;
                this.openDialog(true, urlRedirect);
              } else {
                this.selectedSubMenu = undefined;
                this.activeSubMenuPath = '';
              }
            } else {
              // tabName is not valid at all — redirect to first menu
              const urlRedirect = `gp/${this.selectedInvestor.id}/vehicle/${this.selectedFund.id}/${this.selectedMenu.path}`;
              this.openDialog(true, urlRedirect);
            }
          } else {
            // Deep URL (length >= 8): /gp/{gpId}/vehicle/{vehicleId}/{tabName}/{tabParam}/{id}/{quarter}
            // e.g. /portfolio-group/quarterly-data/{quarterlyDataId}/{quarter}
            // Just sync sidebar state — do NOT redirect, let the component handle the detail view
            this.menuItems.forEach(item => {
              if ('subMenus' in item && item.path === this.selectedMenu.path) {
                item.isToggled = true;
              }
            });

            if (this.isValidPath(tabName)) {
              const subMenus = this.filterValidSubmenus();
              if (subMenus.some((x: any) => x.path === tabParam)) {
                // Valid deep URL — sync state only, no redirect
                this.selectedSubMenu = tabParam;
                this.activeSubMenuPath = tabParam;
              } else if (subMenus.length > 0) {
                // tabParam not valid — redirect to first valid submenu
                const firstSubmenuPath = subMenus[0].path;
                const urlRedirect = `gp/${this.selectedInvestor.id}/vehicle/${this.selectedFund.id}/${this.selectedMenu.path}/${firstSubmenuPath}`;
                this.selectedSubMenu = firstSubmenuPath;
                this.activeSubMenuPath = firstSubmenuPath;
                this.openDialog(true, urlRedirect);
              } else {
                this.selectedSubMenu = undefined;
                this.activeSubMenuPath = '';
              }
            } else {
              // tabName not valid — redirect to first menu
              const urlRedirect = `gp/${this.selectedInvestor.id}/vehicle/${this.selectedFund.id}/${this.selectedMenu.path}`;
              this.openDialog(true, urlRedirect);
            }
          }
        }
      } else if (!isValidGpId()) {
        this.defaultGPSelection(false);
      }
    } else {
      if (urlSegments.length === 2 && gpName === '') {
        this.defaultGPSelection(true);
      } else if (gpName === 'gp' && vehicleName !== 'vehicle') {
        if (isValidGpId()) {
          this.selectedInvestor = isValidGpId();
          this.selectedFund = this.reorderPrivileges(
            this.selectedInvestor.vehicles
          )[0];
          this.selectedMenu = this.menuItems.filter(
            x => x.key === this.selectedFund.privileges[0]
          )[0];
          if (vehicleName !== undefined) {
            const urlRedirect = `gp/${this.selectedInvestor.id}/vehicle/${this.selectedFund.id}/${this.selectedMenu.path}`;
            this.openDialog(true, urlRedirect);
          } else {
            this.router.navigate([
              `gp/${this.selectedInvestor.id}/vehicle/${this.selectedFund.id}/${this.selectedMenu.path}`,
            ]);
          }
        } else {
          if (!gpId) {
            this.defaultGPSelection(true);
          } else {
            this.defaultGPSelection(false);
          }
        }
      } else {
        urlSegments.length <= 6 && this.defaultGPSelection(false);
      }
    }

    // Unified menu filtering — works for all menus including group menus
    if (!this.selectedFund || !this.selectedMenu) {
      return;
    }

    this.menuItems = this.menuItems.filter((x: any) => {
      if (x.subMenus && x.subMenus.length > 0) {
        x.subMenus = x.subMenus.filter(
          (subMenu: any) =>
            this.selectedFund.privileges.includes(subMenu.key) ||
            this.selectedFund.subPrivilegeIds?.includes(subMenu.key)
        );
        return x.subMenus.length > 0;
      }
      return this.selectedFund.privileges.includes(x.key);
    });

    // Onboarding-only view: hide the sections a GP still in onboarding has no use
    // for. Deliberately placed AFTER the privilege filter above -- and therefore
    // after isValidPath() has already run earlier in this handler -- so this only
    // affects what the sidebar renders. Hidden sections stay reachable by direct
    // URL and never trip the Access Denied dialog.
    if (environment.onboardingOnlyMode) {
      this.menuItems = this.menuItems
        .filter((x: any) => ONBOARDING_ONLY_MENU_KEYS.includes(x.key))
        .map((x: any) => {
          if (x.subMenus) {
            x.subMenus = x.subMenus.filter((subMenu: any) =>
              ONBOARDING_ONLY_MENU_KEYS.includes(subMenu.key)
            );
          }
          return x;
        });
    }

    this.userService.setFundAdminCockpitUrl(
      this.selectedFund.fundAdminCockpitUrl
    );
    this.syncTenantBranding(this.selectedInvestor, this.selectedFund);
    this.userService.setSelectedVehicle(this.selectedFund);
    this.commonService.setSecondaryPath([]);
    this.persistFund = this.selectedFund;
    this.persistInvestor = this.selectedInvestor;
  }

  handleNavigationStart(event: NavigationStart) {
    const urlSegments = this.getPathSegments(event.url);
    const gpId = urlSegments[2];
    const gp = this.baseData?.gps?.find((x: any) => x.id === gpId);
    if (gp) {
      this.userService.setIsMainUser(gp.isMainUser);
    }
  }

  private getPathSegments(url: string): string[] {
    return url.split('?')[0].split('#')[0].split('/');
  }

  defaultGPSelection(isInitial: boolean) {
    this.selectedInvestor = this.baseData.gps[0];
    this.funds = this.reorderPrivileges(this.selectedInvestor.vehicles);
    this.selectedFund = this.funds[0];
    this.selectedMenu = this.menuItems.filter(
      x => x.key === this.selectedFund.privileges[0]
    )[0];
    if (isInitial) {
      this.router.navigate([
        `gp/${this.selectedInvestor.id}/vehicle/${this.selectedFund.id}/${this.selectedMenu.path}`,
      ]);
    } else {
      const urlRedirect = `gp/${this.selectedInvestor.id}/vehicle/${this.selectedFund.id}/${this.selectedMenu.path}`;
      this.openDialog(true, urlRedirect);
    }
  }

  isValidPath(tabName: string): boolean {
    // Direct match on top-level menu path (dashboard, management, transactions, settings)
    const directMatch = this.menuItems
      .filter(x => this.selectedFund.privileges.find((y: any) => y === x.key))
      .filter(z => z.path === tabName)[0];

    if (directMatch) return true;

    // Check if it's a valid sub-menu path (allows /quarterly-data/... to work)
    const subMenuMatch = this.menuItems.some(item =>
      item.subMenus?.some(
        (sub: any) =>
          sub.path === tabName &&
          (this.selectedFund.privileges.includes(sub.key) ||
            this.selectedFund.subPrivilegeIds?.includes(sub.key))
      )
    );

    if (subMenuMatch) return true;

    // For group menus (portfolio-group, workspace-group): check privileged submenus
    const groupMatch = this.menuItems.find(
      x =>
        x.path === tabName &&
        x.subMenus?.some(
          (sub: any) =>
            this.selectedFund.privileges.includes(sub.key) ||
            this.selectedFund.subPrivilegeIds?.includes(sub.key)
        )
    );

    return groupMatch !== undefined;
  }

  // Checks privileges and subPrivilegeIds for group menus
  filterValidSubmenus(): any[] {
    const subMenus = Array.isArray(this.selectedMenu?.subMenus)
      ? this.selectedMenu.subMenus
      : [];
    const filteredSubMenus = subMenus.filter(
      (x: any) =>
        this.selectedFund.privileges.includes(x.key) ||
        this.selectedFund.subPrivilegeIds?.includes(x.key)
    );
    return filteredSubMenus;
  }

  checkPrivileges(userPreviligeData: string[], menuItem: any): boolean {
    if (!userPreviligeData) return false;
    return userPreviligeData.some(privilege => privilege === menuItem.key);
  }

  openDialog(event: boolean, redirectUrl: any) {
    if (event) {
      this.isInaccessible = true;
      const dialogRef = this.dialog.open(DialogComponent, {
        data: <ModalDialogData>{
          titleKey: 'Access Denied',
          contentKey: 'access denied',
          positiveMessageKey: 'OK',
          negativeMessageKey: '',
          isValid: '1',
        },
        disableClose: true,
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result === '1') {
          this.redirectToValidURL(redirectUrl);
          this.isInaccessible = false;
        }
      });
    } else {
      const dialogRef = this.dialog.open(DialogComponent, {
        data: <ModalDialogData>{
          titleKey: 'settings.logOut',
          contentKey: 'settings.logoutConfirmation',
          positiveMessageKey: 'yesMessage',
          negativeMessageKey: 'cancelMessage',
          currentGP: this.selectedInvestor,
          isValid: '0',
        },
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result === '1') {
          this.authService.logout();
        }
      });
    }
  }

  redirectToValidURL(redirectUrl: any) {
    this.router.navigate([redirectUrl]);
  }

  openTermsOfUse() {
    this.dialog.open(TermsOfUseComponent, {
      panelClass: 'term-of-use-panel',
      data: {
        confirmedTermsOfUse: this.sessionUser.events.confirmedTermsOfUse,
      },
      disableClose: !this.sessionUser.events.confirmedTermsOfUse,
    });
  }

  breadCrumbNavigate(path: string[], primary: string, type: string) {
    if (type === 'primary') {
      this.router.navigate(
        [
          `gp/${this.selectedInvestor.id}/vehicle/${
            this.selectedFund.id
          }/${path[0].split(' ').join('-').toLowerCase()}`,
        ],
        { onSameUrlNavigation: 'reload' }
      );
    } else if (primary.toLowerCase() === 'new reports') {
      const label = path[0];
      const reportType = REPORT_TYPE_CATALOG.find(
        item => this.translate.instant(item.labelKey) === label
      );
      if (reportType) {
        this.router.navigate(
          [
            'gp',
            this.selectedInvestor.id,
            'vehicle',
            this.selectedFund.id,
            'portfolio-group',
            'new-reports',
            reportType.slug,
          ],
          { onSameUrlNavigation: 'reload' }
        );
      }
    } else {
      const endPath =
        primary === 'Documents'
          ? Base64.encode(`/Documents/${path.join('/')}`)
          : path.map(x => x.toLowerCase()).join('/');
      this.router.navigate(
        [
          `gp/${this.selectedInvestor.id}/vehicle/${
            this.selectedFund.id
          }/${primary.toLowerCase()}/${endPath}`,
        ],
        { onSameUrlNavigation: 'reload' }
      );
    }
  }

  hideBankAccount(key: string) {
    if (['viewBankTransactions', 'viewFinoBankAccounts'].includes(key)) {
      if (environment.layer === 'prod') {
        return true;
      }
      return [
        '95c19d8c-7a98-4bfd-81d8-cc8b7d0ab3a3',
        'a62cfbe0-658a-492f-a3e6-de5f16dce4fd',
        'b8a69254-5d57-42cd-81fd-bce6b9329aef',
        '2801c07a-9b8e-4f17-aaae-cb50cf21c728',
        'bfe3ab72-4b05-48f8-b60b-7edc3ed4ee55',
        'e782dafc-4fea-4365-b07c-12c2e7b05a61',
        'fcac0345-d33f-4315-8edd-f2c4a8b195a0',
        '90d1e3f1-db9e-4c16-a324-fe832731a258',
        '9c0caf3d-7cc9-4a44-8f43-70138075cc50',
        'bfe3ab72-4b05-48f8-b60b-7edc3ed4ee55',
        'e782dafc-4fea-4365-b07c-12c2e7b05a61',
        'fcac0345-d33f-4315-8edd-f2c4a8b195a0',
      ].includes(this.selectedFund.id);
    }
    return true;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.tenantBrandingService.reset();
    if (this.baseDataSubscription) {
      this.baseDataSubscription.unsubscribe();
    }
  }

  decodeRandomString(encodedString: string): {
    randomString: string;
    gpId: string;
    vehicleId: string;
  } {
    const decodedData = Base64.decode(encodedString);
    const [randomString, gpId, vehicleId] = decodedData.split(':');
    return { randomString, gpId, vehicleId };
  }
}
